import { DraftInlineStyleType, RawDraftContentBlock, RawDraftContentState, RawDraftInlineStyleRange } from 'draft-js';
import { getOr, indexOf, isArray, isEqual, isNull, isUndefined, max, set, uniq } from 'lodash/fp';

import { OpinionInstance, OpinionInstanceContent, OpinionInstanceField, OpinionInstanceFieldType } from './types';

const getMergedBlockKeyOrder = (previousBlockKeys: string[], currentBlockKeys: string[]): string[] => {
    const common = new Set(previousBlockKeys.filter(key => currentBlockKeys.includes(key)));
    const result: string[] = [];
    let previousBlockIterator = 0;
    let currentBlockIterator = 0;
    while (previousBlockIterator < previousBlockKeys.length || currentBlockIterator < currentBlockKeys.length) {
        const previousBlockKey = previousBlockKeys[previousBlockIterator];
        const currentBlockKey = currentBlockKeys[currentBlockIterator];

        // Both pointers are at the same common key
        if (previousBlockKey === currentBlockKey && common.has(previousBlockKey)) {
            result.push(previousBlockKey);
            previousBlockIterator++;
            currentBlockIterator++;
            continue;
        }

        // Emit unique keys from A
        if (previousBlockIterator < previousBlockKeys.length && (!common.has(previousBlockKey) || previousBlockKey !== currentBlockKey)) {
            if (!common.has(previousBlockKey)) {
                result.push(previousBlockKey);
                previousBlockIterator++;
                continue;
            }
        }

        // Emit unique keys from B
        if (currentBlockIterator < currentBlockKeys.length && (!common.has(currentBlockKey) || previousBlockKey !== currentBlockKey)) {
            if (!common.has(currentBlockKey)) {
                result.push(currentBlockKey);
                currentBlockIterator++;
                continue;
            }
        }

        // Safety advance to avoid infinite loops
        previousBlockIterator++;
        currentBlockIterator++;
    }

    return result;
};

// LCS (Longest Common Subsequence) with a dynamic programming table finds the longest sequence of characters present in two strings
// Maintaining relative order but not necessarily contiguity, by filling a 2D grid (table) that stores lengths of LCS for all subproblems
// Using a simple rule:
// if characters match, add 1 to the diagonal;
// if they don't, take the max from above or left
// Ultimately finding the length in the bottom-right cell and backtracking to build the sequence
// Read More Here: https://www.geeksforgeeks.org/dsa/longest-common-subsequence-dp-4/
const getLCSWord = (previousSplitText: string[], currentSplitText: string[]): number[][] => {
    const dynamicProgrammingTable: number[][] = Array.from({ length: previousSplitText.length + 1 }, () =>
        Array(currentSplitText.length + 1).fill(0)
    );
    for (let previousTextIndex = previousSplitText.length - 1; previousTextIndex >= 0; previousTextIndex--) {
        for (let currentTextIndex = currentSplitText.length - 1; currentTextIndex >= 0; currentTextIndex--) {
            dynamicProgrammingTable[previousTextIndex][currentTextIndex] =
                previousSplitText[previousTextIndex] === currentSplitText[currentTextIndex]
                    ? 1 + dynamicProgrammingTable[previousTextIndex + 1][currentTextIndex + 1]
                    : Math.max(dynamicProgrammingTable[previousTextIndex + 1][currentTextIndex], dynamicProgrammingTable[previousTextIndex][currentTextIndex + 1]);
        }
    }
    return dynamicProgrammingTable;
};

enum CharacterMergeType {
    EQUAL = 'equal',
    REMOVED = 'removed',
    ADDED = 'added'
}

interface CharacterOperation {
    type: CharacterMergeType;
    previousTextWord?: string;
    currentTextWord?: string;
}

export const getTextWithDifference = (previousText: string, currentText: string) => {
    // Handle empty cases
    if (previousText.length === 0 && currentText.length === 0) {
        return { updatedText: '', newCharacters: [], removedCharacters: [] };
    }
    if (previousText.length === 0) {
        return {
            updatedText: currentText,
            newCharacters: Array.from({ length: currentText.length }, (_, i) => i),
            removedCharacters: []
        };
    }
    if (currentText.length === 0) {
        return {
            updatedText: previousText,
            newCharacters: [],
            removedCharacters: Array.from({ length: previousText.length }, (_, i) => i)
        };
    }
    // Work at word level for diff/merge
    const previousSplitText = previousText.split(' ');
    const currentSplitText = currentText.split(' ');

    const dynamicProgrammingTable = getLCSWord(previousSplitText, currentSplitText);

    // Backtrack to get the diff
    let previousTextIndex = 0;
    let currentTextIndex = 0;
    let operationIndex = 0;
    let updatedText = '';
    let newCharacters: number[] = [];
    let removedCharacters: number[] = [];
    const characterOperations: CharacterOperation[] = [];
    while (previousTextIndex < previousSplitText.length && currentTextIndex < currentSplitText.length) {
        if (previousSplitText[previousTextIndex] === currentSplitText[currentTextIndex]) {
            characterOperations.push({ type: CharacterMergeType.EQUAL, previousTextWord: previousSplitText[previousTextIndex] });
            previousTextIndex++;
            currentTextIndex++;
        } else if (dynamicProgrammingTable[previousTextIndex + 1][currentTextIndex] >= dynamicProgrammingTable[previousTextIndex][currentTextIndex + 1]) {
            characterOperations.push({ type: CharacterMergeType.REMOVED, previousTextWord: previousSplitText[previousTextIndex] });
            previousTextIndex++;
        } else {
            characterOperations.push({ type: CharacterMergeType.ADDED, currentTextWord: currentSplitText[currentTextIndex] });
            currentTextIndex++;
        }
    }
    while (previousTextIndex < previousSplitText.length) {
        characterOperations.push({ type: CharacterMergeType.REMOVED, previousTextWord: previousSplitText[previousTextIndex] });
        previousTextIndex++;
    }
    while (currentTextIndex < currentSplitText.length) {
        characterOperations.push({ type: CharacterMergeType.ADDED, currentTextWord: currentSplitText[currentTextIndex] });
        currentTextIndex++;
    }

    while (operationIndex < characterOperations.length) {
        if (characterOperations[operationIndex].type === CharacterMergeType.EQUAL) {
            if (updatedText.length > 0) {
                updatedText += ' ';
            }
            updatedText += characterOperations[operationIndex].previousTextWord!;
            operationIndex++;
        } else {
            // Gather consecutive removes and adds
            const removes: string[] = [];
            const adds: string[] = [];
            while (operationIndex < characterOperations.length && characterOperations[operationIndex].type === CharacterMergeType.REMOVED) {
                removes.push(characterOperations[operationIndex].previousTextWord!);
                operationIndex++;
            }
            while (operationIndex < characterOperations.length && characterOperations[operationIndex].type === CharacterMergeType.ADDED) {
                adds.push(characterOperations[operationIndex].currentTextWord!);
                operationIndex++;
            }
            // Pair them for substitution, then handle leftovers
            const pairs = Math.max(removes.length, adds.length);
            for (let pairIndex = 0; pairIndex < pairs; pairIndex++) {
                if (updatedText.length > 0) {
                    updatedText += ' ';
                }
                const removeWord = removes[pairIndex] || '';
                const addWord = adds[pairIndex] || '';
                let start = updatedText.length;
                updatedText += removeWord + addWord;
                // Mark removed chars
                for (let removeCharacterIndex = 0; removeCharacterIndex < removeWord.length; ++removeCharacterIndex) {
                    removedCharacters.push(start + removeCharacterIndex);
                }
                // Mark new chars
                for (let newCharacterIndex = 0; newCharacterIndex < addWord.length; ++newCharacterIndex) {
                    newCharacters.push(start + removeWord.length + newCharacterIndex);
                }
            }
        }
    }

    return { updatedText, newCharacters, removedCharacters };
};

const getConsecutiveNumberOffset = (modifiedIndexes: number[], inlineStyledCharacter: number) => {
    const maxInline = max(modifiedIndexes.filter(modifiedIndex => inlineStyledCharacter >= modifiedIndex));
    if (isUndefined(maxInline)) {
        return 0;
    }
    let offsetIndex = indexOf(maxInline, modifiedIndexes);
    let isConsecutive = true;
    while (offsetIndex < modifiedIndexes.length && isConsecutive) {
        isConsecutive = !isUndefined(modifiedIndexes[offsetIndex + 1]);
        if (isConsecutive) {
            offsetIndex++;
        }
    }
    return offsetIndex;
};

const updateInlineStyledCharacters = (inlineStyledCharacters: number[], removedCharacters: number[], newCharacters: number[], isPrevious: boolean) => inlineStyledCharacters.map(styledCharacter => {
    if (isPrevious) {
        if (newCharacters.length === 0) {
            return styledCharacter;
        }
        const offsetNewNumber = getConsecutiveNumberOffset(newCharacters, styledCharacter);
        return styledCharacter + offsetNewNumber;
    }

    const offsetRemovedNumber = getConsecutiveNumberOffset(removedCharacters, styledCharacter);
    return styledCharacter + offsetRemovedNumber;
});

const getAffectedCharacters = (length: number, offset: number) => Array(length).fill(offset).map((val, index) => val + index);

export const getInlineStyledCharacters = (inlineStyleRanges: RawDraftInlineStyleRange[] | undefined) => {
    let boldCharacters: number[] = [];
    let italicCharacters: number[] = [];
    let underlineCharacters: number[] = [];
    if (inlineStyleRanges && inlineStyleRanges.length > 0) {
        inlineStyleRanges.sort((a, b) => a.offset - b.offset).forEach(({ style, length, offset }) => {
            if (style === 'BOLD') {
                boldCharacters = [...boldCharacters, ...getAffectedCharacters(length, offset)];
            }
            if (style === 'ITALIC') {
                italicCharacters = [...italicCharacters, ...getAffectedCharacters(length, offset)];
            }
            if (style === 'UNDERLINE') {
                underlineCharacters = [...underlineCharacters, ...getAffectedCharacters(length, offset)];
            }
        });
    }
    return { boldCharacters, italicCharacters, underlineCharacters };
};

const getMergedBlockInlineStyleCharacters = (previousValueBlock: RawDraftContentBlock | null, currentValueBlock: RawDraftContentBlock | null, removedCharacters: number[], newCharacters: number[]) => {
    const previousInlineStyleCharacters = getInlineStyledCharacters(previousValueBlock?.inlineStyleRanges);
    const currentInlineStyleCharacters = getInlineStyledCharacters(currentValueBlock?.inlineStyleRanges);
    const updatedPreviousBold = updateInlineStyledCharacters(previousInlineStyleCharacters.boldCharacters, removedCharacters, newCharacters, true);
    const updatedCurrentBold = updateInlineStyledCharacters(currentInlineStyleCharacters.boldCharacters, removedCharacters, newCharacters, false);
    const updatedPreviousUnderline = updateInlineStyledCharacters(previousInlineStyleCharacters.underlineCharacters, removedCharacters, newCharacters, true);
    const updatedCurrentUnderline = updateInlineStyledCharacters(currentInlineStyleCharacters.underlineCharacters, removedCharacters, newCharacters, false);
    const updatedPreviousItalic = updateInlineStyledCharacters(previousInlineStyleCharacters.italicCharacters, removedCharacters, newCharacters, true);
    const updatedCurrentItalic = updateInlineStyledCharacters(currentInlineStyleCharacters.italicCharacters, removedCharacters, newCharacters, false);
    const boldCharacters = uniq([...updatedPreviousBold, ...updatedCurrentBold]);
    const underlineCharacters = uniq([...updatedPreviousUnderline, ...updatedCurrentUnderline]);
    const italicCharacters = uniq([...updatedPreviousItalic, ...updatedCurrentItalic]);
    return { boldCharacters, underlineCharacters, italicCharacters };
};

interface DraftInlineStyleRange {
    offset: number;
    length: number;
}

const getModifiedCharactersDraftInlineStyle = (characterIndexes: number[]): DraftInlineStyleRange[] => {
    if (characterIndexes.length === 0) return [];

    const inlineStyleRanges: DraftInlineStyleRange[] = [];

    let offset = characterIndexes[0];
    let length = 1;

    for (let i = 1; i < characterIndexes.length; i++) {
        if (characterIndexes[i] === characterIndexes[i - 1] + 1) {
            length++;
        } else {
            inlineStyleRanges.push({ offset, length });
            offset = characterIndexes[i];
            length = 1;
        }
    }

    inlineStyleRanges.push({ offset, length });

    return inlineStyleRanges;
};

const addProvidedInlineStyleToCharacters = (characterIndexes: number[], style: DraftInlineStyleType): RawDraftInlineStyleRange[] => {
    const emptyInlineStyles = getModifiedCharactersDraftInlineStyle(characterIndexes);
    return emptyInlineStyles.map(inlineStyle => ({ ...inlineStyle, style }));
};

const RGB_MANDATORY = 'rgb(52, 155, 58)';
const RGB_RED = 'rgb(255, 52, 73)';
const getUpdatedInlineStyleRanges = (removedCharacters: number[], newCharacters: number[], boldCharacters: number[], underlineCharacters: number[], italicCharacters: number[]): RawDraftInlineStyleRange[] => {
    const strikeThroughCharactersInlineStyle = addProvidedInlineStyleToCharacters(removedCharacters, 'STRIKETHROUGH');
    const removedCharactersInlineStyle = addProvidedInlineStyleToCharacters(removedCharacters, `color-${RGB_RED}` as DraftInlineStyleType);
    const newCharactersInlineStyle = addProvidedInlineStyleToCharacters(newCharacters, `color-${RGB_MANDATORY}` as DraftInlineStyleType);
    const boldCharactersInlineStyle = addProvidedInlineStyleToCharacters(boldCharacters, 'BOLD');
    const underlineCharactersInlineStyle = addProvidedInlineStyleToCharacters(underlineCharacters, 'UNDERLINE');
    const italicCharactersInlineStyle = addProvidedInlineStyleToCharacters(italicCharacters, 'ITALIC');
    return [
        ...strikeThroughCharactersInlineStyle,
        ...removedCharactersInlineStyle,
        ...newCharactersInlineStyle,
        ...boldCharactersInlineStyle,
        ...underlineCharactersInlineStyle,
        ...italicCharactersInlineStyle
    ];
};

const getMergedTextBlocks = (blockKeys: string[], previousValue: RawDraftContentState | null, currentValue: RawDraftContentState | null): RawDraftContentBlock[] => blockKeys.map(blockKey => {
    const previousValueBlock = !isNull(previousValue) && previousValue.blocks.find(({ key }) => key === blockKey) || null;
    const currentValueBlock = !isNull(currentValue) && currentValue.blocks.find(({ key }) => key === blockKey) || null;
    const { removedCharacters, newCharacters, updatedText } = getTextWithDifference(previousValueBlock?.text || '', currentValueBlock?.text || '');
    const { boldCharacters, underlineCharacters, italicCharacters } = getMergedBlockInlineStyleCharacters(previousValueBlock, currentValueBlock, removedCharacters, newCharacters);
    const updatedInlineStyleRanges: RawDraftInlineStyleRange[] = getUpdatedInlineStyleRanges(removedCharacters, newCharacters, boldCharacters, underlineCharacters, italicCharacters);
    return {
        text: updatedText,
        inlineStyleRanges: updatedInlineStyleRanges,
        key: blockKey,
        depth: currentValueBlock?.depth || previousValueBlock?.depth || 0,
        type: 'unstyled',
        data: {},
        entityRanges: []
    };
});

export const compareWysiwygValues = (previousValue: RawDraftContentState | null, currentValue: RawDraftContentState | null): RawDraftContentState | null => {
    const valueHasUpdated = !isEqual(previousValue, currentValue);
    if (!valueHasUpdated) {
        return currentValue;
    }
    const previousBlockKeys = isNull(previousValue) ? [] : previousValue.blocks.map(({ key }) => key);
    const currentBlockKeys = isNull(currentValue) ? [] : currentValue.blocks.map(({ key }) => key);
    const mergedBlockKeys = getMergedBlockKeyOrder(previousBlockKeys, currentBlockKeys);
    const mergedBlocks = getMergedTextBlocks(mergedBlockKeys, previousValue, currentValue);
    return { blocks: mergedBlocks, entityMap: {} };
};

const compareInstanceContent = (previousContent: OpinionInstanceContent, savedContent: OpinionInstanceContent): OpinionInstanceContent => Object.entries(savedContent).reduce((acc: OpinionInstanceContent, [sectionId, fields]: [string, { [fieldId: string]: OpinionInstanceField }]) => {
    Object.entries(fields).forEach(([fieldId, fieldContent]) => {
        if (isArray(fieldContent)) {
            acc = set(`[${sectionId}][${fieldId}]`, fieldContent, acc);
        } else {
            const singleField = fieldContent as OpinionInstanceField;
            if (singleField.type === OpinionInstanceFieldType.DROPDOWN_DETAILS) {
                const previousValue = getOr(null, `[${sectionId}][${fieldId}].wysiwygValue`, previousContent) as RawDraftContentState | null;
                const mergedWysiwyg = compareWysiwygValues(previousValue, singleField.wysiwygValue);
                const mergedField = set('wysiwygValue', mergedWysiwyg, singleField);
                acc = set(`[${sectionId}][${fieldId}]`, mergedField, acc);
            } else if (singleField.type === OpinionInstanceFieldType.WYSIWYG) {
                const previousValue = getOr(null, `[${sectionId}][${fieldId}].value`, previousContent) as RawDraftContentState | null;
                const mergedWysiwyg = compareWysiwygValues(previousValue, singleField.value);
                const mergedField = set('value', mergedWysiwyg, singleField);
                acc = set(`[${sectionId}][${fieldId}]`, mergedField, acc);
            } else {
                acc = set(`[${sectionId}][${fieldId}]`, singleField, acc);
            }
        }
    });
    return acc;
}, {} as OpinionInstanceContent) as unknown as OpinionInstanceContent;

export const calculateBlacklineInstance = (previousInstance: OpinionInstance, savedInstance: OpinionInstance) => set('content', compareInstanceContent(previousInstance.content, savedInstance.content), savedInstance);
